import React, { useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { X } from 'lucide-react';
import logger from '../../utils/logger';
import { useNavigationHistory } from '../../hooks/useNavigationHistory';

// Wallet configurations with periodic table style info
const WALLET_CONFIG = {
  'Phantom': { symbol: 'Ph', number: 46, name: 'Phantom' },
  'Solflare': { symbol: 'Sf', number: 38, name: 'Solflare' },
  'Backpack': { symbol: 'Bp', number: 5, name: 'Backpack' },
  'Brave Wallet': { symbol: 'Br', number: 35, name: 'Brave' },
  'Coinbase Wallet': { symbol: 'Cb', number: 27, name: 'Coinbase' },
  'Trust Wallet': { symbol: 'Tw', number: 22, name: 'Trust' },
  'Ledger': { symbol: 'Lg', number: 3, name: 'Ledger' },
  'Torus': { symbol: 'To', number: 90, name: 'Torus' },
  'MathWallet': { symbol: 'Mw', number: 12, name: 'Math' },
  'Slope': { symbol: 'Sl', number: 14, name: 'Slope' },
  'Sollet': { symbol: 'St', number: 16, name: 'Sollet' },
  'Glow': { symbol: 'Gw', number: 32, name: 'Glow' },
  'Exodus': { symbol: 'Ex', number: 2, name: 'Exodus' },
};

const getWalletConfig = (walletName) => {
  return WALLET_CONFIG[walletName] || {
    symbol: walletName.substring(0, 2).toUpperCase(),
    number: Math.floor(Math.random() * 100) + 1,
    name: walletName.split(' ')[0]
  };
};

const WalletTile = ({ wallet, onClick, isDetected }) => {
  const config = getWalletConfig(wallet.adapter.name);
  
  return (
    <button
      onClick={onClick}
      style={{
        position: 'relative',
        width: '70px',
        height: '70px',
        borderRadius: '3px',
        background: 'linear-gradient(135deg, #1E7B34 0%, #145A25 50%, #0D3D18 100%)',
        border: '1px solid rgba(30, 123, 52, 0.6)',
        boxShadow: '2px 3px 0 rgba(0, 0, 0, 0.5), 0 0 10px rgba(30, 123, 52, 0.2)',
        cursor: 'pointer',
        padding: 0,
        margin: 0,
        outline: 'none',
        overflow: 'hidden',
        transition: 'all 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'linear-gradient(135deg, #2E8B3E 0%, #1E7B34 50%, #145A25 100%)';
        e.currentTarget.style.boxShadow = '2px 3px 0 rgba(0, 0, 0, 0.5), 0 0 20px rgba(30, 123, 52, 0.4)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'linear-gradient(135deg, #1E7B34 0%, #145A25 50%, #0D3D18 100%)';
        e.currentTarget.style.boxShadow = '2px 3px 0 rgba(0, 0, 0, 0.5), 0 0 10px rgba(30, 123, 52, 0.2)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Top highlight */}
      <div style={{
        position: 'absolute',
        top: '1px',
        left: '1px',
        right: '1px',
        height: '25px',
        borderRadius: '2px 2px 0 0',
        background: 'linear-gradient(to bottom, rgba(129, 199, 132, 0.5) 0%, rgba(102, 187, 106, 0.2) 40%, transparent 100%)',
        pointerEvents: 'none',
      }} />
      {/* Left highlight */}
      <div style={{
        position: 'absolute',
        top: '1px',
        left: '1px',
        width: '20px',
        bottom: '1px',
        borderRadius: '2px 0 0 2px',
        background: 'linear-gradient(to right, rgba(129, 199, 132, 0.4) 0%, rgba(102, 187, 106, 0.15) 40%, transparent 100%)',
        pointerEvents: 'none',
      }} />
      {/* Atomic number */}
      <span style={{
        position: 'absolute',
        top: '4px',
        right: '5px',
        fontSize: '9px',
        fontWeight: 600,
        color: 'rgba(255, 255, 255, 0.7)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        lineHeight: 1,
      }}>
        {config.number}
      </span>
      {/* Detected indicator */}
      {isDetected && (
        <span style={{
          position: 'absolute',
          top: '4px',
          left: '5px',
          fontSize: '6px',
          fontWeight: 700,
          color: '#C4A000',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          lineHeight: 1,
          textTransform: 'uppercase',
        }}>
          ●
        </span>
      )}
      {/* Element symbol */}
      <span style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        marginTop: '-4px',
        fontSize: '24px',
        fontWeight: 700,
        color: 'white',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        lineHeight: 1,
      }}>
        {config.symbol}
      </span>
      {/* Element name */}
      <span style={{
        position: 'absolute',
        bottom: '5px',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '7px',
        fontWeight: 500,
        color: 'rgba(255, 255, 255, 0.7)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}>
        {config.name}
      </span>
    </button>
  );
};

const CustomWalletModal = ({ visible, onClose }) => {
  const { wallets, select, connect } = useWallet();
  const { pushView, popView } = useNavigationHistory();
  
  // Track modal in navigation history
  useEffect(() => {
    if (visible) {
      pushView('wallet-modal', 'Select Wallet');
    }
    return () => {
      if (visible) {
        popView();
      }
    };
  }, [visible, pushView, popView]);
  
  if (!visible) return null;
  
  const handleWalletSelect = async (walletName) => {
    try {
      select(walletName);
      // Small delay to let the adapter initialize after selection
      setTimeout(async () => {
        try {
          await connect();
        } catch (e) {
          // Connection will be handled by autoConnect or user action
        }
      }, 100);
      onClose();
    } catch (e) {
      logger.error('Wallet selection error', { error: e.message });
    }
  };
  
  // Separate detected and other wallets
  const detectedWallets = wallets.filter(w => w.readyState === 'Installed');
  const otherWallets = wallets.filter(w => w.readyState !== 'Installed');
  
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        pointerEvents: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'linear-gradient(145deg, #0a0a0a 0%, #111111 50%, #0a0a0a 100%)',
          border: '2px solid rgba(30, 123, 52, 0.5)',
          borderRadius: '4px',
          boxShadow: '0 0 30px rgba(30, 123, 52, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
          padding: '20px',
          maxWidth: '340px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}>
          <h2 style={{
            margin: 0,
            fontFamily: "'Bebas Neue', 'Roboto Condensed', sans-serif",
            fontSize: '20px',
            letterSpacing: '2px',
            color: '#ffffff',
            textTransform: 'uppercase',
          }}>
            Select Wallet
          </h2>
          <button
            onClick={onClose}
            aria-label="Close wallet modal"
            style={{
              background: 'rgba(30, 123, 52, 0.2)',
              border: '1px solid rgba(30, 123, 52, 0.4)',
              borderRadius: '2px',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <X size={16} color="#1E7B34" />
          </button>
        </div>
        
        {/* Detected Wallets */}
        {detectedWallets.length > 0 && (
          <>
            <p style={{
              margin: '0 0 10px 0',
              fontSize: '10px',
              color: 'rgba(255, 255, 255, 0.5)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}>
              Detected
            </p>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              marginBottom: '20px',
            }}>
              {detectedWallets.map((wallet) => (
                <WalletTile
                  key={wallet.adapter.name}
                  wallet={wallet}
                  isDetected={true}
                  onClick={() => handleWalletSelect(wallet.adapter.name)}
                />
              ))}
            </div>
          </>
        )}
        
        {/* Other Wallets */}
        {otherWallets.length > 0 && (
          <>
            <p style={{
              margin: '0 0 10px 0',
              fontSize: '10px',
              color: 'rgba(255, 255, 255, 0.5)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}>
              {detectedWallets.length > 0 ? 'More Wallets' : 'Available Wallets'}
            </p>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
            }}>
              {otherWallets.slice(0, 8).map((wallet) => (
                <WalletTile
                  key={wallet.adapter.name}
                  wallet={wallet}
                  isDetected={false}
                  onClick={() => handleWalletSelect(wallet.adapter.name)}
                />
              ))}
            </div>
          </>
        )}
        
        {wallets.length === 0 && (
          <p style={{
            textAlign: 'center',
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '12px',
            padding: '20px 0',
          }}>
            No wallets detected. Please install a Solana wallet.
          </p>
        )}
      </div>
    </div>
  );
};

export default CustomWalletModal;
